
/*
The page is not replaced until animation out has been active for a while -
so it won't show old page if the page is received very fast from server.
Also - no matter how fast a new page loads, we will give the animation-out and animation-in all the time they need.
 */

import { computed, defineComponent, Ref, ref, nextTick, watch } from 'vue';
import { pageResolver } from '@/core/component-resolver.service';
import pageStore from '@/shared/content/page.store';
import { B2BContentPageViewModel } from '@/types/B2BcontentServerContract';
import { SpaPageRenderedEventKey, SpaAwaitingPageEventKey, SpaForceConfigurationReloadEventKey } from '@/core/router';
import bus from '@/core/bus';
import contentApi from '@/shared/content/content-api.service';
import { HttpCancelError } from '@/core/http/HttpCancelError';
import logService from '@/core/logging.service';
import { useRoute } from 'vue-router';
import { NOT_FOUND } from 'http-status-codes';
import domService from '@/core/browser/dom.service';
import { B2CContentPageViewModel, B2CPublicContentPageViewModel } from '@/types/B2CcontentServerContract';
import { RedirectError } from '@/core/http/redirect.interceptor';
import MyBoliaLeftNavigation from '@/b2c/navigation/MyBoliaLeftNavigation.vue';
import breakpointsState from '@/core/responsive/breakpoints/breakpoints-state.observable';
import PageRenderTransitionHandler from '@/shared/content/PageRenderTransitionHandler.vue';

let animationOutFiredHandle: number | null = null;
let minimumAnimationTimeFired: number | null = null;

type TimerState = 'done' | 'awaitingAnimationOut' | 'awaitingMinimumAnimationTime';
type PageDataState = 'done' | 'awaitingNewPage' | 'newPageReceived';

export const pageLeftNavigationActiveBreakpointKey = 'min-lg';

export default defineComponent({
    name: 'PageRender',
    components: { MyBoliaLeftNavigation, PageRenderTransitionHandler },
    setup() {
        const route = useRoute();
        const pageDataState: Ref<PageDataState> = ref('done');
        const timerState: Ref<TimerState> = ref('done');
        const awaitingPage: Ref<B2BContentPageViewModel | B2CContentPageViewModel | B2CPublicContentPageViewModel | null> = ref(null);

        const alias = computed(() => {
            return pageStore.contentType;
        });

        const resolvedPage = computed(() => {
            return alias.value ? pageResolver.resolve(alias.value) : null;
        });

        const content = computed(() => {
            return pageStore.content;
        });

        const isPageWithLeftNavigation: Ref<boolean> = computed(() => {
            return pageStore.hasLeftNavigation &&
                breakpointsState.isBreakpointActive(pageLeftNavigationActiveBreakpointKey);
        });

        const myBoliaLeftNavigationEnabled: Ref<boolean> = computed(() => {
            return pageStore.myBoliaLeftNavigationEnabled;
        });

        const isChangingPage = computed(() => {
            const done = timerState.value === 'done' && pageDataState.value === 'done';
            return !done;
        });

        const getTimerStateForTSBug = computed(() => {
            // https://github.com/microsoft/TypeScript/issues/29155
            return timerState.value;
        });

        const pageAltTitle = computed(() => {
            return pageStore.altTitle;
        });

        const renderNewPage = (initialPageLoad: boolean) => {
            pageStore.updatePage(awaitingPage.value);
            awaitingPage.value = null;
            nextTick(() => {
                // Page should be rendered.
                pageDataState.value = 'done';
                // Fire event for router (scrolling).
                bus.emit(SpaPageRenderedEventKey, { initialPageLoad });
            });
        };

        const animationOutFired = (initialPageLoad: boolean) => {
            timerState.value = 'awaitingMinimumAnimationTime';
            animationOutFiredHandle = null;
            if (pageDataState.value === 'newPageReceived') {
                renderNewPage(initialPageLoad);
            }
        };

        const minimumAnimationTimeFiredFunc = () => {
            minimumAnimationTimeFired = null;
            timerState.value = 'done';
        };

        const getPage = async(relativeUrl: string, initialPageLoad: boolean = false) => {
            try {
                if (animationOutFiredHandle) {
                    clearTimeout(animationOutFiredHandle);
                }
                if (minimumAnimationTimeFired) {
                    clearTimeout(minimumAnimationTimeFired);
                }
                pageDataState.value = 'awaitingNewPage';
                timerState.value = 'awaitingAnimationOut';
                bus.emit(SpaAwaitingPageEventKey);

                animationOutFiredHandle = setTimeout(() => animationOutFired(initialPageLoad), 150); // Delay before we replace page (if new page ready).
                minimumAnimationTimeFired = setTimeout(() => minimumAnimationTimeFiredFunc(), 400); // Animation out + animation in time.

                awaitingPage.value = await contentApi.getPage(relativeUrl, initialPageLoad);
                pageDataState.value = 'newPageReceived';
                if (getTimerStateForTSBug.value === 'awaitingMinimumAnimationTime' || getTimerStateForTSBug.value === 'done') {
                    renderNewPage(initialPageLoad);
                }
            } catch (error) {
                if (error instanceof HttpCancelError || error instanceof RedirectError) {
                    // Ignore - just because of new page-request while waiting.
                } else if (error.status === NOT_FOUND) {
                    // trigger server rendered 404 page
                    location.reload();
                } else {
                    logService.exception('Got exception when loading page', error);
                    pageStore.updatePage(null);
                }
            }
        };

        const initPage = async() => {
            await getPage(route.fullPath, true);
        };

        initPage();

        watch(pageAltTitle, () => {
            domService.setPageTitle(pageStore.title);
        }, { immediate: true });

        watch(() => route.path, (newPath, oldPath) => {
            if (newPath !== oldPath) {
                getPage(route.fullPath);
            }
        });

        const forcePDPConfigurationSilentReload = () => {
            getPage(route.fullPath);
        };

        bus.on(SpaForceConfigurationReloadEventKey, forcePDPConfigurationSilentReload);

        return {
            isChangingPage,
            alias,
            resolvedPage,
            content,
            myBoliaLeftNavigationEnabled,
            isPageWithLeftNavigation,
        };
    },
});

